import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API });

// Auto-inject bearer token (set by lib/auth.jsx via localStorage)
http.interceptors.request.use((config) => {
  const t = (typeof localStorage !== "undefined" && localStorage.getItem("cxa.token")) || null;
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// On 401, clear the bad token and broadcast so AuthProvider can flip to login.
http.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401 && typeof window !== "undefined") {
      if (localStorage.getItem("cxa.token")) {
        localStorage.removeItem("cxa.token");
        window.dispatchEvent(new CustomEvent("cxa:unauthorized"));
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Projects
  listProjects: (q) => http.get("/projects", { params: q ? { q } : {} }).then((r) => r.data),
  createProject: (body) => http.post("/projects", body).then((r) => r.data),
  updateProject: (id, body) => http.put(`/projects/${id}`, body).then((r) => r.data),
  deleteProject: (id) => http.delete(`/projects/${id}`).then((r) => r.data),
  getProject: (id) => http.get(`/projects/${id}`).then((r) => r.data),

  // Documents
  listDocs: (projectId, params = {}) =>
    http.get(`/projects/${projectId}/documents`, { params }).then((r) => r.data),
  getDoc: (id) => http.get(`/documents/${id}`).then((r) => r.data),
  uploadDoc: (projectId, file, tags = "") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tags", tags);
    return http
      .post(`/projects/${projectId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  deleteDoc: (id) => http.delete(`/documents/${id}`).then((r) => r.data),

  // AI
  aiReview: (body) => http.post("/ai/review", body).then((r) => r.data),
  aiScript: (body) => http.post("/ai/script", body).then((r) => r.data),
  getChat: (projectId, mode) =>
    http.get(`/projects/${projectId}/chat`, { params: { mode } }).then((r) => r.data),
  clearChat: (projectId, mode) =>
    http.delete(`/projects/${projectId}/chat`, { params: { mode } }).then((r) => r.data),

  // Scripts library
  listScripts: (projectId, q) =>
    http.get(`/projects/${projectId}/scripts`, { params: q ? { q } : {} }).then((r) => r.data),
  getScript: (id) => http.get(`/scripts/${id}`).then((r) => r.data),
  createScript: (projectId, body) =>
    http.post(`/projects/${projectId}/scripts`, body).then((r) => r.data),
  renameScript: (id, name) => http.put(`/scripts/${id}`, { name }).then((r) => r.data),
  deleteScript: (id) => http.delete(`/scripts/${id}`).then((r) => r.data),
  addScriptVersion: (id, body) => http.post(`/scripts/${id}/versions`, body).then((r) => r.data),
  iterateScript: (id, body) => http.post(`/scripts/${id}/iterate`, body).then((r) => r.data),

  // Admin / user management
  listUsers: () => http.get("/admin/users").then((r) => r.data),
  createUser: (body) => http.post("/admin/users", body).then((r) => r.data),
  resetUserPassword: (id, new_password) =>
    http.post(`/admin/users/${id}/reset-password`, { new_password }).then((r) => r.data),
  deleteUser: (id) => http.delete(`/admin/users/${id}`).then((r) => r.data),
  changeMyPassword: (current_password, new_password) =>
    http.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  shareProject: (id, user_email) =>
    http.post(`/projects/${id}/share`, { user_email }).then((r) => r.data),
  unshareProject: (id, user_email) =>
    http.post(`/projects/${id}/unshare`, { user_email }).then((r) => r.data),

  // CxAlloy
  cxalloyGenerate: async (body) => {
    const start = await http.post("/cxalloy/generate", body);
    return await pollCxAlloyJob(start.data.job_id);
  },
  cxalloyExport: (spec, format, force = false) =>
    http
      .post("/cxalloy/export", { spec, format, force }, { responseType: "blob" })
      .then((r) => r.data),
  cxalloyExportLink: (spec, format, force = false) =>
    http.post("/cxalloy/export-link", { spec, format, force }).then((r) => r.data),
  cxalloyIterate: async (scriptId, body) => {
    const start = await http.post(`/scripts/${scriptId}/cxalloy_iterate`, body);
    return await pollCxAlloyJob(start.data.job_id);
  },
  cxalloyValidate: (spec) =>
    http.post("/cxalloy/validate", { spec }).then((r) => r.data),
  cxalloyAutofix: (spec) =>
    http.post("/cxalloy/autofix", { spec }).then((r) => r.data),
};

async function pollCxAlloyJob(jobId, { intervalMs = 2000, timeoutMs = 5 * 60 * 1000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const { data } = await http.get(`/cxalloy/jobs/${jobId}`);
    if (data.status === "done") return data;
    if (data.status === "error") {
      const err = new Error(data.error || "AI job failed");
      err.response = { data: { detail: data.error || "AI job failed" } };
      throw err;
    }
  }
  throw new Error("AI job timed out after 5 minutes");
}
